export interface ExameParaOrdenacao {
  id?: number;
  nome?: string;
  exame_nome?: string;
  obrigatorio?: boolean;
}

/**
 * Ordena exames seguindo a regra de ouro:
 * 1. Exame Clínico primeiro
 * 2. Obrigatórios em ordem alfabética
 * 3. Opcionais em ordem alfabética
 */
export function ordenarExamesPadrao<T extends ExameParaOrdenacao>(exames: T[]): T[] {
  if (!exames || exames.length === 0) return exames;

  // Função auxiliar para obter nome do exame
  const getNomeExame = (exame: T): string => {
    return (exame.exame_nome || exame.nome || '').toLowerCase().trim();
  };

  // Função auxiliar para verificar se é Exame Clínico
  const isExameClinico = (exame: T): boolean => {
    const nome = getNomeExame(exame);
    return (
      nome === 'exame clínico' ||
      nome === 'exame clinico' ||
      nome.includes('clínico') ||
      nome.includes('clinico') ||
      nome.includes('aso')
    );
  };

  // Separar exames em grupos
  const exameClinico = exames.find(isExameClinico);
  const obrigatorios = exames.filter(
    (e) => !isExameClinico(e) && (e.obrigatorio === true)
  );
  const opcionais = exames.filter(
    (e) => !isExameClinico(e) && (e.obrigatorio !== true)
  );

  // Ordenar obrigatórios e opcionais alfabeticamente
  const obrigatoriosOrdenados = obrigatorios.sort((a, b) => {
    const nomeA = getNomeExame(a);
    const nomeB = getNomeExame(b);
    return nomeA.localeCompare(nomeB, 'pt-BR');
  });

  const opcionaisOrdenados = opcionais.sort((a, b) => {
    const nomeA = getNomeExame(a);
    const nomeB = getNomeExame(b);
    return nomeA.localeCompare(nomeB, 'pt-BR');
  });

  // Retornar na ordem: Exame Clínico, Obrigatórios, Opcionais
  return [
    ...(exameClinico ? [exameClinico] : []),
    ...obrigatoriosOrdenados,
    ...opcionaisOrdenados,
  ];
}

/**
 * Formata o tipo de exame para o padrão ASO (ex: "ASO Admissional", "ASO Demissional", "ASO Retorno ao Trabalho")
 */
export function formatAsoTipo(tipoNome?: string): string {
  if (!tipoNome) return 'ASO Ocupacional';
  const clean = tipoNome.trim();
  if (/^aso\b/i.test(clean)) {
    return clean;
  }
  return `ASO ${clean}`;
}

/**
 * Converte nomes de exames em ALL CAPS para Title Case amigável,
 * preservando siglas conhecidas da área médica e de segurança do trabalho
 */
export function formatarNomeExameAmigavel(nome?: string): string {
  if (!nome) return '';
  const trimmed = nome.trim();
  // Se já tiver minúsculas e maiúsculas mescladas, manter como está
  if (/[a-z]/.test(trimmed) && /[A-Z]/.test(trimmed)) {
    return trimmed;
  }

  const siglas = new Set([
    'aso', 'ppf', 'ecg', 'eeg', 'pgr', 'pcmso', 'vdrl', 'tgo', 'tgp',
    'hcv', 'hbsag', 'hiv', 'pa', 'oit', 'nr', 'cbo', 'rh', 'dna'
  ]);
  const preposicoes = new Set([
    'de', 'da', 'do', 'das', 'dos', 'e', 'em', 'com', 'por', 'para', 'na', 'no', '1º', '2º'
  ]);

  return trimmed
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((word, idx) => {
      const lower = word.toLowerCase();
      if (siglas.has(lower)) {
        return lower.toUpperCase();
      }
      if (idx > 0 && preposicoes.has(lower)) {
        return lower;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

